import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Clock3, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { STATUS_LABELS } from "@/constants/roles";
import { ApprovalChain } from "@/pages/LeavePage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";

const statusPillCls = {
  pending: "bg-signal-absent text-white",
  approved: "bg-signal-ok text-white",
  rejected: "bg-signal-late text-white",
};

export default function OvertimePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: null, start_time: "18:00", end_time: "21:00", reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/overtime/mine");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ date: null, start_time: "18:00", end_time: "21:00", reason: "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      date: new Date(r.date + "T00:00:00"),
      start_time: r.start_time,
      end_time: r.end_time,
      reason: r.reason || "",
    });
    setOpen(true);
  };
  const doDelete = async (r) => {
    if (!window.confirm(`Hapus pengajuan lembur ${r.date}?`)) return;
    try {
      await api.delete(`/overtime/${r.request_id}`);
      toast.success("Pengajuan dihapus.");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Gagal hapus."); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) return toast.error("Pilih tanggal.");
    // Client-side guard: date >= today (WIB)
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const picked = new Date(form.date.getFullYear(), form.date.getMonth(), form.date.getDate());
    if (picked < today) return toast.error("Tanggal lembur tidak boleh lebih kecil dari hari ini.");
    const payload = {
      date: format(form.date, "yyyy-MM-dd"),
      start_time: form.start_time, end_time: form.end_time, reason: form.reason,
    };
    try {
      if (editing) {
        await api.patch(`/overtime/${editing.request_id}`, payload);
        toast.success("Pengajuan diperbarui.");
      } else {
        await api.post("/overtime", payload);
        toast.success("Pengajuan dikirim.");
      }
      setOpen(false); setEditing(null);
      setForm({ date: null, start_time: "18:00", end_time: "21:00", reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Gagal.");
    }
  };

  return (
    <div data-testid={TESTIDS.overtimePage} className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tiny">Lembur</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Pengajuan Lembur Saya</h1>
        </div>
        <button data-testid={TESTIDS.overtimeNewBtn} onClick={openCreate}
          className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 hover:bg-black transition-colors text-sm font-semibold">
          <Clock3 className="w-4 h-4" /> Ajukan Lembur
        </button>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table data-testid={TESTIDS.overtimeTable} className="w-full text-sm min-w-[900px]">
          <thead className="bg-black text-white text-left">
            <tr><Th>Tanggal</Th><Th>Jam</Th><Th>Alasan</Th><Th>Status</Th><Th>Progress</Th><Th>Aksi</Th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-gray-500">Memuat...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">Belum ada pengajuan.</td></tr>}
            {items.map((r) => (
              <tr key={r.request_id} className="border-t border-black/10 hover:bg-gray-50 align-top">
                <Td className="font-mono">{r.date}</Td>
                <Td className="font-mono">{r.start_time} – {r.end_time}</Td>
                <Td className="max-w-xs text-gray-600">{r.reason}</Td>
                <Td><span className={`px-2 py-1 text-xs font-bold uppercase tracking-widest ${statusPillCls[r.status]}`}>{STATUS_LABELS[r.status]}</span></Td>
                <Td><ApprovalChain approvers={r.approvers} currentStep={r.current_step} /></Td>
                <Td>
                  {r.status === "pending" ? (
                    <div className="flex items-center gap-1">
                      <button data-testid={`overtime-edit-${r.request_id}`} onClick={() => openEdit(r)} className="border border-black/20 hover:bg-black hover:text-white p-1.5" title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`overtime-delete-${r.request_id}`} onClick={() => doDelete(r)} className="border border-black/20 hover:bg-signal-late hover:text-white p-1.5" title="Hapus">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : <span className="text-xs text-gray-400">—</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="rounded-none max-w-lg border-black/20">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Lembur" : "Ajukan Lembur"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Tanggal">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start rounded-none border-black/20 bg-white hover:bg-gray-50">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {form.date ? format(form.date, "dd MMM yyyy") : "Pilih tanggal"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar mode="single" selected={form.date} onSelect={(d) => setForm({ ...form, date: d })}
                    disabled={(d) => { const n = new Date(); return d < new Date(n.getFullYear(), n.getMonth(), n.getDate()); }}
                    initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Jam Mulai"><input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="input font-mono" placeholder="18:00" /></Field>
              <Field label="Jam Selesai"><input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input font-mono" placeholder="21:00" /></Field>
            </div>
            <Field label="Alasan"><textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required className="input" /></Field>
            <DialogFooter>
              <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="border border-black/20 px-4 py-2 hover:bg-gray-100 text-sm">Batal</button>
              <button data-testid={TESTIDS.overtimeSubmit} type="submit" className="bg-klein text-white px-5 py-2 hover:bg-black text-sm font-semibold">{editing ? "Simpan" : "Ajukan"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <style>{`.input { width:100%; border: 1px solid rgba(0,0,0,0.2); padding: 10px 12px; background: white; font-size: 14px; }`}</style>
    </div>
  );
}
function Field({ label, children }) { return <div><div className="label-tiny mb-2">{label}</div>{children}</div>; }
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
